import { captureException } from '@mentimeter/errors/sentry';
import { MentiError } from '@mentimeter/error-utils/error';
import type { Question } from '@mentimeter/legacy-series-types/question';
import type { StaticContentWithInteractiveContentStyling as EditorStaticContentWithInteractiveContentStyling } from '../../compatibility-overrides/editor-types';
import {
  type CompatibilityInteractiveContent,
  type CompatibilitySlide,
} from '../../compatibility-types';
import {
  CORRECT_ANSWER_MODE_DISABLED,
  MULTIPLE_VOTE_POLICIES,
  SCORING_TIME_BASED_TYPE,
} from '../../constants';
import {
  correctAnswerModeToQuestionShowCorrect,
  responseModeToQuestionActive,
} from '../converters/interactive-content';
import { visualizationToQuestionSubType } from '../converters/interactive-content/visualization-to-question-subtype';
import { responseVisibilityToQuestionHideResults } from '../converters/slide';
import { handleNoInteractiveContentError } from '../../applications/common/handle-no-interactive-content-error';
import { getCachedChoicesProperty } from './utils/get-cached-choices-property';

const INTERACTIVE_QUESTION_PROPERTIES = [
  'question',
  'question_description',
  'multiple_votes',
  'choices',
  'show_correct',
  'sub_type',
  'active',
  'time_based_scoring',
  'countdown_to_vote',
  'hide_results',
] as const satisfies Array<keyof Question>;

type InteractiveContentStyling =
  EditorStaticContentWithInteractiveContentStyling['interactiveContentStyling'];

export type InteractiveQuestionProperty =
  (typeof INTERACTIVE_QUESTION_PROPERTIES)[number];

export const getInteractiveQuestionProperty = (
  slide: CompatibilitySlide,
  prop: InteractiveQuestionProperty,
) => {
  const interactiveContent = slide.interactiveContents?.[0];

  const staticContent =
    slide.staticContent as EditorStaticContentWithInteractiveContentStyling;

  if (!interactiveContent) {
    return handleNoInteractiveContentError(slide, prop);
  }

  switch (prop) {
    case 'question':
      return interactiveContent.title;
    case 'question_description':
      return interactiveContent.description;
    case 'multiple_votes': {
      return isMultipleVotes(interactiveContent);
    }
    case 'choices': {
      return getCachedChoicesProperty(slide, interactiveContent);
    }
    case 'show_correct':
      return getShowCorrect(interactiveContent);
    case 'sub_type': {
      return getQuestionSubType(staticContent.interactiveContentStyling);
    }
    case 'time_based_scoring': {
      return interactiveContent.scoring === SCORING_TIME_BASED_TYPE;
    }
    case 'countdown_to_vote': {
      return interactiveContent.countdown;
    }
    case 'active': {
      return responseModeToQuestionActive(interactiveContent.responseMode);
    }
    case 'hide_results':
      return responseVisibilityToQuestionHideResults(slide.responseVisibility);
    default:
      const error = new MentiError(
        `Unhandled interactive question property: \`${String(prop)}\``,
        {
          feature: 'compatibility-layer',
        },
      );
      captureException(error);

      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(error.message);
      }
  }
};

export const isInteractiveQuestionProperty = (
  prop: keyof Question,
): prop is InteractiveQuestionProperty => {
  return INTERACTIVE_QUESTION_PROPERTIES.includes(
    prop as InteractiveQuestionProperty,
  );
};

const getQuestionSubType = (
  interactiveContentStyling: InteractiveContentStyling,
) => {
  if (
    interactiveContentStyling &&
    'visualization' in interactiveContentStyling
  ) {
    return visualizationToQuestionSubType(
      interactiveContentStyling.visualization,
    );
  }

  return null;
};

const getShowCorrect = (
  interactiveContent: CompatibilityInteractiveContent,
) => {
  return correctAnswerModeToQuestionShowCorrect(
    interactiveContent.correctAnswerMode ?? CORRECT_ANSWER_MODE_DISABLED,
  );
};

const isMultipleVotes = (
  interactiveContent: CompatibilityInteractiveContent,
) => {
  return MULTIPLE_VOTE_POLICIES.includes(interactiveContent.responsePolicy);
};
